import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Search, MessageCircle, Loader2, Brain, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SearchQAPanel = () => {
  const [question, setQuestion] = useState('');
  const [docText, setDocText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [qaResult, setQaResult] = useState(null);
  const [searchResult, setSearchResult] = useState(null);
  const [relResult, setRelResult] = useState(null);
  const [activeTab, setActiveTab] = useState('qa');
  const token = localStorage.getItem('reasondms_token');

  const askQuestion = async () => {
    if (!question.trim() || !docText.trim()) { toast.error('Enter both question and document text'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/enterprise/qa`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, documents: [{ title: 'Document', text: docText }] })
      });
      if (!res.ok) {
        let errorMsg = 'QA failed';
        try { errorMsg = (await res.json()).detail || errorMsg; } catch { errorMsg = res.status === 404 ? 'Enterprise AI service not available' : `Server error (${res.status})`; }
        throw new Error(errorMsg);
      }
      setQaResult(await res.json());
      toast.success('Answer found');
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const runSearch = async () => {
    if (!searchQuery.trim() || !docText.trim()) { toast.error('Enter search query and document text'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/enterprise/search/semantic`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery, documents: [{ title: 'Document', text: docText }] })
      });
      if (!res.ok) {
        let errorMsg = 'Search failed';
        try { errorMsg = (await res.json()).detail || errorMsg; } catch { errorMsg = res.status === 404 ? 'Enterprise AI service not available' : `Server error (${res.status})`; }
        throw new Error(errorMsg);
      }
      const searchData = await res.json();
      setSearchResult(searchData);
      toast.success('Search complete');
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const extractRelationships = async () => {
    if (!docText.trim()) { toast.error('Enter document text'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/enterprise/relationship/extract`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: docText, doc_type: 'general' })
      });
      if (!res.ok) {
        let errorMsg = 'Extraction failed';
        try { errorMsg = (await res.json()).detail || errorMsg; } catch { errorMsg = res.status === 404 ? 'Enterprise AI service not available' : `Server error (${res.status})`; }
        throw new Error(errorMsg);
      }
      setRelResult(await res.json());
      toast.success('Entities extracted');
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      {/* Document Context */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          <label className="text-xs font-medium text-slate-500">Document Context</label>
          <textarea
            data-testid="search-doc-input"
            value={docText}
            onChange={e => setDocText(e.target.value)}
            className="w-full h-28 text-sm p-3 border rounded-lg font-mono resize-y focus:ring-2 focus:ring-cyan-300 outline-none"
            placeholder="Paste document text here..."
          />
          <div className="flex gap-2">
            <button onClick={() => setActiveTab('qa')} className={`text-xs px-3 py-1.5 rounded-full ${activeTab === 'qa' ? 'bg-cyan-100 text-cyan-700 font-medium' : 'bg-slate-100 text-slate-500'}`}>
              <MessageCircle className="h-3 w-3 inline mr-1" />Question Answering
            </button>
            <button onClick={() => setActiveTab('search')} className={`text-xs px-3 py-1.5 rounded-full ${activeTab === 'search' ? 'bg-cyan-100 text-cyan-700 font-medium' : 'bg-slate-100 text-slate-500'}`}>
              <Search className="h-3 w-3 inline mr-1" />Semantic Search
            </button>
            <button onClick={() => setActiveTab('relations')} className={`text-xs px-3 py-1.5 rounded-full ${activeTab === 'relations' ? 'bg-cyan-100 text-cyan-700 font-medium' : 'bg-slate-100 text-slate-500'}`}>
              <Brain className="h-3 w-3 inline mr-1" />Relationships
            </button>
          </div>
        </CardContent>
      </Card>

      {/* QA Tab */}
      {activeTab === 'qa' && (
        <Card>
          <CardContent className="pt-4 space-y-3">
            <div className="flex gap-2">
              <input
                data-testid="qa-question-input"
                value={question}
                onChange={e => setQuestion(e.target.value)}
                className="flex-1 text-sm p-2.5 border rounded-lg focus:ring-2 focus:ring-cyan-300 outline-none"
                placeholder="Ask a question about the document..."
                onKeyDown={e => e.key === 'Enter' && askQuestion()}
              />
              <Button data-testid="qa-ask-btn" onClick={askQuestion} disabled={loading} className="bg-cyan-600 hover:bg-cyan-700">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            {qaResult?.success && (
              <div data-testid="qa-result" className="space-y-2">
                <div className="p-3 bg-cyan-50 rounded-lg text-sm text-slate-700">{qaResult.answer}</div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-cyan-100 text-cyan-700 text-xs">Confidence: {Math.round((qaResult.confidence || 0) * 100)}%</Badge>
                  {qaResult.source_documents?.map((s, i) => <Badge key={i} variant="outline" className="text-xs">{s}</Badge>)}
                </div>
                {qaResult.follow_up_questions?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {qaResult.follow_up_questions.map((q, i) => (
                      <button key={i} onClick={() => { setQuestion(q); }} className="text-xs px-2 py-1 bg-slate-100 rounded-full text-slate-600 hover:bg-slate-200">{q}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Semantic Search Tab */}
      {activeTab === 'search' && (
        <Card>
          <CardContent className="pt-4 space-y-3">
            <div className="flex gap-2">
              <input
                data-testid="semantic-search-input"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="flex-1 text-sm p-2.5 border rounded-lg focus:ring-2 focus:ring-cyan-300 outline-none"
                placeholder="Search across documents semantically..."
                onKeyDown={e => e.key === 'Enter' && runSearch()}
              />
              <Button data-testid="semantic-search-btn" onClick={runSearch} disabled={loading} className="bg-cyan-600 hover:bg-cyan-700">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
            {searchResult?.success && (
              <div data-testid="search-result" className="space-y-2">
                <p className="text-xs text-slate-500">{searchResult.total_matches} matches - "{searchResult.query_interpretation}"</p>
                {searchResult.results?.map((r, i) => (
                  <div key={i} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-slate-700">{r.document_title}</span>
                      <Badge className="bg-cyan-100 text-cyan-700 text-xs">{Math.round((r.relevance_score || 0) * 100)}%</Badge>
                      <Badge variant="outline" className="text-xs">{r.match_type}</Badge>
                    </div>
                    <p className="text-xs text-slate-600">{r.matched_text}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Relationships Tab */}
      {activeTab === 'relations' && (
        <Card>
          <CardContent className="pt-4 space-y-3">
            <Button data-testid="extract-relations-btn" onClick={extractRelationships} disabled={!docText.trim() || loading} className="bg-cyan-600 hover:bg-cyan-700">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Brain className="h-4 w-4 mr-2" />}
              Extract Entities & Relationships
            </Button>
            {relResult?.success && (
              <div data-testid="relations-result" className="space-y-3">
                <div className="flex gap-3 text-xs text-slate-500">
                  <span>{relResult.graph_summary?.total_entities || relResult.entities?.length || 0} entities</span>
                  <span>{relResult.graph_summary?.total_relationships || relResult.relationships?.length || 0} relationships</span>
                </div>
                {relResult.entities?.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {relResult.entities.map((e, i) => (
                      <div key={i} className="p-2 bg-slate-50 rounded text-xs">
                        <Badge className="bg-cyan-100 text-cyan-700 text-xs mb-1">{e.type}</Badge>
                        <p className="font-medium text-slate-700">{e.name}</p>
                      </div>
                    ))}
                  </div>
                )}
                {relResult.relationships?.length > 0 && (
                  <div className="space-y-1">
                    {relResult.relationships.map((r, i) => (
                      <div key={i} className="text-xs p-2 bg-blue-50 rounded flex items-center gap-2">
                        <span className="font-medium">{r.from_entity}</span>
                        <span className="text-blue-500">{r.relationship?.replace(/_/g, ' ')}</span>
                        <span className="font-medium">{r.to_entity}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SearchQAPanel;
